"use client";

import { Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  composeGuidedSearchQuery,
  LEVEL_OPTIONS,
  NEED_OPTIONS,
  SUBJECT_OPTIONS,
  type GuidedChipGroup,
  type LevelId,
  type NeedId,
  type SubjectId,
} from "@/app/components/guided-search-options";

type GuidedCloseReason = "backdrop" | "button" | "escape";

const NEEDS_WITH_LEVEL_AND_SUBJECT = new Set<NeedId>([
  "notes",
  "tuition",
  "practicals",
]);

function optionLabel<T extends string>(
  options: Array<{ id: T; label: string }>,
  id: T | null
) {
  return options.find((option) => option.id === id)?.label;
}

function GuidedChip({
  label,
  isSelected,
  onClick,
  children,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onClick}
      className={`inline-flex min-h-10 items-center rounded-full border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
        isSelected
          ? "border-blue-700 bg-blue-700 text-white shadow-sm"
          : "border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50"
      }`}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

export default function HomeGuidedSearchPanel({
  isOpen,
  initialQuery,
  isSubmitting,
  onClose,
  onChipToggle,
  onInputStart,
  onSubmit,
}: {
  isOpen: boolean;
  initialQuery: string;
  isSubmitting: boolean;
  onClose: (reason: GuidedCloseReason) => void;
  onChipToggle: (payload: {
    group: GuidedChipGroup;
    id: string;
    isSelected: boolean;
  }) => void;
  onInputStart: () => void;
  onSubmit: (
    query: string,
    metadata: { guidedChipCount: number }
  ) => void;
}) {
  const [freeText, setFreeText] = useState(initialQuery);
  const [selectedNeed, setSelectedNeed] = useState<NeedId | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<LevelId | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<SubjectId | null>(
    null
  );
  const [hasEntered, setHasEntered] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHasEntered(false);
      return;
    }
    setFreeText(initialQuery);
    setSelectedNeed(null);
    setSelectedLevel(null);
    setSelectedSubject(null);
    const animationFrame = window.requestAnimationFrame(() => {
      setHasEntered(true);
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [initialQuery, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose("escape");
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const composedQuery = useMemo(
    () =>
      composeGuidedSearchQuery({
        freeText,
        need: selectedNeed,
        level: selectedLevel,
        subject: selectedSubject,
      }),
    [freeText, selectedLevel, selectedNeed, selectedSubject]
  );
  const hasQuery = composedQuery.length > 0;
  const hasManualQuery =
    freeText.trim().length > 0 &&
    selectedNeed === null &&
    selectedLevel === null &&
    selectedSubject === null;
  const showGuidedDetails =
    selectedNeed !== null && NEEDS_WITH_LEVEL_AND_SUBJECT.has(selectedNeed);
  const showNeedStep = selectedNeed === null;
  const showLevelStep = showGuidedDetails && selectedLevel === null;
  const showSubjectStep =
    showGuidedDetails && selectedLevel !== null && selectedSubject === null;
  const isGuidedComplete =
    selectedNeed !== null &&
    (!showGuidedDetails ||
      (selectedLevel !== null && selectedSubject !== null));
  const selectedTrail = [
    optionLabel(NEED_OPTIONS, selectedNeed),
    optionLabel(LEVEL_OPTIONS, selectedLevel),
    optionLabel(SUBJECT_OPTIONS, selectedSubject),
  ].filter(Boolean);

  if (!isOpen || typeof document === "undefined") return null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasQuery) return;
    onSubmit(composedQuery, {
      guidedChipCount: [selectedNeed, selectedLevel, selectedSubject].filter(
        Boolean
      ).length,
    });
  };

  const resetGuidedSearch = () => {
    if (selectedNeed) {
      onChipToggle({ group: "need", id: selectedNeed, isSelected: false });
    }
    if (selectedLevel) {
      onChipToggle({ group: "level", id: selectedLevel, isSelected: false });
    }
    if (selectedSubject) {
      onChipToggle({ group: "subject", id: selectedSubject, isSelected: false });
    }
    setFreeText("");
    setSelectedNeed(null);
    setSelectedLevel(null);
    setSelectedSubject(null);
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[10000] bg-slate-950/35 transition-opacity duration-200 motion-reduce:transition-none md:hidden ${
        hasEntered ? "opacity-100" : "opacity-0"
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="home-guided-search-title"
    >
      <button
        type="button"
        aria-label="Close guided search"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={() => onClose("backdrop")}
      />
      <form
        action="/blog"
        method="get"
        onSubmit={handleSubmit}
        className="absolute inset-x-0 bottom-0 max-h-[92dvh] overflow-y-auto rounded-t-3xl bg-slate-50 px-4 pb-5 pt-4 shadow-[0_-20px_55px_rgba(15,23,42,0.28)]"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="pt-1">
            <h2
              id="home-guided-search-title"
              className="text-2xl font-bold text-slate-950"
            >
              Find what you need
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close guided search"
            onClick={() => onClose("button")}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>

        <label htmlFor="home-guided-search-q" className="sr-only">
          Search by topic, subject, or programme
        </label>
        <div className="relative mt-5">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500"
          />
          <input
            id="home-guided-search-q"
            name="q"
            type="search"
            value={composedQuery}
            inputMode="search"
            enterKeyHint="search"
            autoComplete="off"
            onChange={(event) => {
              if (event.currentTarget.value.trim().length > 0) {
                onInputStart();
              }
              setFreeText(event.currentTarget.value);
              setSelectedNeed(null);
              setSelectedLevel(null);
              setSelectedSubject(null);
            }}
            className="h-14 w-full rounded-2xl border border-slate-300 bg-white pl-12 pr-4 text-base text-slate-900 shadow-sm placeholder:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            placeholder="Search a topic, subject, or programme"
          />
        </div>

        {!hasManualQuery && selectedTrail.length > 0 ? (
          <div className="mt-5 flex items-center justify-between gap-3 rounded-xl bg-blue-50 px-3 py-2.5">
            <p className="min-w-0 truncate text-sm font-semibold text-blue-950">
              {selectedTrail.join(" / ")}
            </p>
            <button
              type="button"
              onClick={resetGuidedSearch}
              className="shrink-0 text-sm font-semibold text-blue-700 underline decoration-blue-200 underline-offset-4"
            >
              Start over
            </button>
          </div>
        ) : null}

        {!hasManualQuery && showNeedStep ? (
          <section className="mt-5" aria-labelledby="guided-search-need-title">
          <h3
            id="guided-search-need-title"
            className="text-sm font-semibold text-slate-800"
          >
              What do you need?
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {NEED_OPTIONS.map(({ id, label, Icon }) => (
              <GuidedChip
                key={id}
                label={label}
                isSelected={selectedNeed === id}
                    onClick={() => {
                    onChipToggle({ group: "need", id, isSelected: true });
                    setSelectedNeed(id);
                    setSelectedLevel(null);
                    setSelectedSubject(null);
                }}
              >
                <Icon aria-hidden="true" className="mr-2 h-4 w-4" />
              </GuidedChip>
            ))}
          </div>
          </section>
        ) : null}

        {!hasManualQuery && showLevelStep ? (
          <section
            className="mt-5"
            aria-labelledby="guided-search-level-title"
          >
          <h3
            id="guided-search-level-title"
            className="text-sm font-semibold text-slate-800"
          >
              What level?
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {LEVEL_OPTIONS.map(({ id, label }) => (
              <GuidedChip
                key={id}
                label={label}
                isSelected={selectedLevel === id}
                onClick={() => {
                    onChipToggle({ group: "level", id, isSelected: true });
                    setSelectedLevel(id);
                    setSelectedSubject(null);
                }}
              />
            ))}
          </div>
          </section>
        ) : null}

        {!hasManualQuery && showSubjectStep ? (
          <section
            className="mt-5"
            aria-labelledby="guided-search-subject-title"
          >
          <h3
            id="guided-search-subject-title"
            className="text-sm font-semibold text-slate-800"
          >
              Which subject?
          </h3>
          <div className="mt-2 flex flex-wrap gap-2">
            {SUBJECT_OPTIONS.map(({ id, label }) => (
              <GuidedChip
                key={id}
                label={label}
                isSelected={selectedSubject === id}
                onClick={() => {
                    onChipToggle({ group: "subject", id, isSelected: true });
                    setSelectedSubject(id);
                }}
              />
            ))}
          </div>
          </section>
        ) : null}

        {hasManualQuery || isGuidedComplete ? (
          <div className="mt-5 border-t border-slate-200 pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !hasQuery}
              className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-blue-700 px-6 text-base font-semibold text-white shadow-sm transition hover:bg-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Search aria-hidden="true" className="mr-2 h-5 w-5" />
              Show results
          </button>
          </div>
        ) : null}
      </form>
    </div>,
    document.body
  );
}
